/**
 * Fetch wrapper used by every typed API client function.
 *
 * Responsibilities:
 *   1. Add `credentials: 'include'` so the session cookie always rides
 *      cross-subdomain.
 *   2. JSON encode bodies and parse JSON responses.
 *   3. Map error responses to typed `ApiError` throws.
 *   4. Inspect every response's `X-API-Version` header. On MAJOR mismatch,
 *      dispatch a `versionmismatch` window event so the SPA can show a
 *      "please refresh" banner. (Minor/patch are informational only.)
 */

import {
  API_VERSION,
  API_VERSION_HEADER,
  type ApiError,
  isApiError,
} from '@eloquor/api-contract';

const API_BASE = readApiBase();

function readApiBase(): string {
  // In dev, `vite.config.ts` proxies /api → :3001. In prod, set
  // `VITE_API_BASE` at build time to e.g. https://eloquorapi.raymondjxu.net.
  // `as string | undefined` because Vite injects the literal at compile time.
  const fromEnv = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '');
  return fromEnv ?? '';
}

/** Thrown by every helper when the server returns a non-2xx. */
export class ApiCallError extends Error {
  readonly status: number;
  readonly apiError: ApiError;
  constructor(status: number, apiError: ApiError) {
    super(apiError.message);
    this.status = status;
    this.apiError = apiError;
    this.name = 'ApiCallError';
  }
}

/** Event name dispatched on `window` when the major API version drifts. */
export const VERSION_MISMATCH_EVENT = 'versionmismatch';

export interface VersionMismatchDetail {
  serverMajor: number;
  serverFull: string;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Will be JSON-encoded; do not also set Content-Type. */
  json?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}/api/v${API_VERSION.major}${path}`;
  const headers = new Headers(options.headers ?? {});
  let body: BodyInit | undefined;
  if (options.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.json);
  }
  const res = await fetch(url, {
    ...options,
    headers,
    body,
    credentials: 'include',
  });
  checkVersionHeader(res);
  return parseJsonOrThrow<T>(res);
}

/** Like `apiFetch` but returns the raw `Response` for SSE / file downloads. */
export async function apiFetchRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const url = `${API_BASE}/api/v${API_VERSION.major}${path}`;
  const headers = new Headers(options.headers ?? {});
  let body: BodyInit | undefined;
  if (options.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.json);
  }
  const res = await fetch(url, {
    ...options,
    headers,
    body,
    credentials: 'include',
  });
  checkVersionHeader(res);
  if (!res.ok) {
    throw await toApiCallError(res);
  }
  return res;
}

/** Multipart upload helper — same wrapping but skips JSON body. */
export async function apiFetchMultipart<T>(
  path: string,
  form: FormData,
  options: Omit<RequestInit, 'body' | 'method'> = {}
): Promise<T> {
  const url = `${API_BASE}/api/v${API_VERSION.major}${path}`;
  const res = await fetch(url, {
    ...options,
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  checkVersionHeader(res);
  return parseJsonOrThrow<T>(res);
}

/**
 * Multipart upload with progress events. Built on XHR (fetch can't surface
 * upload progress in browsers — only the `Streams` body workaround works,
 * and it's not portable enough to rely on here).
 *
 * Mirrors `apiFetchMultipart` semantics: `credentials: include`, X-API-Version
 * inspected on response, non-2xx mapped to `ApiCallError`.
 */
export async function apiFetchMultipartWithProgress<T>(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void
): Promise<T> {
  const url = `${API_BASE}/api/v${API_VERSION.major}${path}`;
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.addEventListener('progress', (ev) => {
      if (ev.lengthComputable && ev.total > 0) {
        onProgress(ev.loaded / ev.total);
      }
    });
    xhr.addEventListener('load', () => {
      const versionHeader = xhr.getResponseHeader(API_VERSION_HEADER);
      if (versionHeader) {
        const major = parseMajor(versionHeader);
        if (major !== null && major !== API_VERSION.major) {
          window.dispatchEvent(
            new CustomEvent(VERSION_MISMATCH_EVENT, {
              detail: { serverMajor: major, serverFull: versionHeader } as VersionMismatchDetail,
            })
          );
        }
      }
      const text = xhr.responseText;
      const status = xhr.status;
      if (status >= 200 && status < 300) {
        if (!text) {
          resolve(undefined as T);
          return;
        }
        try {
          resolve(JSON.parse(text) as T);
        } catch (e) {
          reject(e);
        }
        return;
      }
      let body: unknown;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        body = null;
      }
      reject(
        new ApiCallError(
          status,
          isApiError(body)
            ? body
            : { code: 'internal_error', message: xhr.statusText || 'Upload failed.' }
        )
      );
    });
    xhr.addEventListener('error', () => {
      reject(new ApiCallError(0, { code: 'internal_error', message: 'Network error.' }));
    });
    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) throw await toApiCallError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function toApiCallError(res: Response): Promise<ApiCallError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = { code: 'internal_error', message: res.statusText || 'Request failed.' };
  }
  if (isApiError(body)) return new ApiCallError(res.status, body);
  return new ApiCallError(res.status, {
    code: 'internal_error',
    message: 'Unexpected error response.',
  });
}

function checkVersionHeader(res: Response): void {
  const header = res.headers.get(API_VERSION_HEADER);
  if (!header) return; // missing — older deploy or non-API call; ignore.
  const major = parseMajor(header);
  if (major !== null && major !== API_VERSION.major) {
    const detail: VersionMismatchDetail = { serverMajor: major, serverFull: header };
    window.dispatchEvent(new CustomEvent(VERSION_MISMATCH_EVENT, { detail }));
  }
}

function parseMajor(version: string): number | null {
  const [maj] = version.split('.');
  const n = Number.parseInt(maj ?? '', 10);
  return Number.isFinite(n) ? n : null;
}
